import { ALL_AUTO_INSTALL_STEP_TYPES, ALL_BUILD_STEP_TYPES, ALL_STEP_NAMES, allNonWrapperTypes, allQuizTypes, allWithNameTypes, allWrapperTypes, } from './constants';
/** Propagated exports. */
export { ALL_BUILD_STEP_TYPES, ALL_TEST_STEP_TYPES, ALL_STEP_NAMES, ALL_AUTO_INSTALL_STEP_TYPES, } from './constants';
/**
 * Check if the given type is a wrapper step type.
 *
 * @param type The type to check if it is a wrapper step type.
 * @returns If the given step is a wrapper step type.
 */
export function isWrapperType(type) {
    return allWrapperTypes.has(type);
}
/**
 * Check if the given step is a wrapper step.
 *
 * @param step The type to check if it is a wrapper step type.
 * @returns If the given step is a wrapper step type.
 */
export function isWrapper(step) {
    return allWrapperTypes.has(step.type);
}
/**
 * Check if the given type is a wrapper step type.
 *
 * @param type The type to check if it is a wrapper step type.
 * @returns If the given step is a wrapper step type.
 */
export function isNonWrapperType(type) {
    return allNonWrapperTypes.has(type);
}
/**
 * Check if the given step is a wrapper step.
 *
 * @param step The type to check if it is a wrapper step type.
 * @returns If the given step is a wrapper step type.
 */
export function isNonWrapper(step) {
    return allNonWrapperTypes.has(step.type);
}
/**
 * Check if the given step is a quiz question step.
 *
 * @param step The step to check.
 * @returns Whether the given step is a quiz question.
 */
export function isQuizQuestion(step) {
    return allQuizTypes.has(step.type);
}
/**
 * Check if the given step is a jupyter notebook step.
 *
 * @param step The step to check.
 * @returns Whether the given step is a jupyter notebook step.
 */
export function isJupyterNotebook(step) {
    return step.type === 'jupyter-notebook';
}
/**
 * Check if the given step has a name.
 *
 * @param step The type to check if it has a name.
 * @returns If the given step has a name.
 */
export function hasName(step) {
    return allWithNameTypes.has(step.type);
}
const buildStepTypeSet = new Set(ALL_BUILD_STEP_TYPES);
const autoInstallStepTypeSet = new Set(ALL_AUTO_INSTALL_STEP_TYPES);
/**
 * Check if the given type is a build step type.
 *
 * @param type The step type to check if it is a build step type.
 * @returns If the given type is a build step.
 */
export function isBuildStepType(type) {
    return buildStepTypeSet.has(type);
}
/**
 * Check if the given step is a build step.
 *
 * @param step The step to check if it is a build step.
 * @returns If the given type is a build step.
 */
export function isBuildStep(step) {
    return buildStepTypeSet.has(step.type);
}
/**
 * Check if the given step is a auto install step.
 *
 * @param step The step to check if it is a auto install step.
 * @returns If the given type is a auto install step.
 */
export function isAutoInstallStep(step) {
    return autoInstallStepTypeSet.has(step.type);
}
/**
 * Get the category of a step.
 *
 * @param step The step to check.
 * @returns The category it belongs in.
 */
export function getStepCategory(step) {
    if (isAutoInstallStep(step)) {
        return 'auto-install';
    }
    else if (isBuildStep(step)) {
        return 'build';
    }
    else {
        return 'test';
    }
}
/**
 * Get the rubric rows connected to this configuration.
 *
 * @param config The configuration to get the rubric rows of.
 * @returns A set of all the connected rubric rows.
 */
export function getConnectedRubricRowIds(config) {
    const res = new Set();
    for (const step of config.steps) {
        if (step.type === 'connect-rubric' && step.rubricRowId.isJust()) {
            res.add(step.rubricRowId.extract());
        }
    }
    return res;
}
/**
 * Creates a sensible name for the provided step to show the user, containing the relevant
 * information they may need.
 *
 * @param step The step.
 * @returns The step header.
 */
export const getStepName = (step) => {
    if (hasName(step) && !/^\s*$/g.test(step.name)) {
        return step.name;
    }
    return ALL_STEP_NAMES[step.type];
};
/** A chunk of bash code. */
export class BashChunk {
    value;
    /** @param value The value of the bash code. */
    constructor(value) {
        this.value = value;
    }
    /**
     * Create a piece of bash code that retrieves a variable.
     *
     * @param varname The name of the variable to retrieve.
     * @returns A piece of bash code that will load the variable.
     */
    static variable(varname) {
        return new BashChunk(`\${${varname}}`);
    }
    /**
     * Quote the given string `inp` as `shlex.quote` from python would quote it.
     *
     * @param value The inputted string that should be quoted.
     * @returns A quoted bash chunk the same as python 3.11 quotes it.
     * @see {@link https://github.com/python/cpython/blob/3.11/Lib/shlex.py#L323-L334}.
     */
    static escape(value) {
        if (value.length === 0) {
            return new BashChunk("''");
        }
        if (/[^\w@%+=:,./-]/.test(value)) {
            const inner = value.replaceAll("'", "'\"'\"'");
            return new BashChunk(`'${inner}'`);
        }
        return new BashChunk(value);
    }
    /**
     * Concat two chunks together.
     *
     * @param other The value to append to this value.
     * @returns A fresh new bash chunk.
     */
    concat(...other) {
        return new BashChunk([this.value, ...other.map(e => e.value)].join(''));
    }
}
